import { Link } from "react-router-dom";
import {
  Phone,
  ArrowRight,
  Check,
  ChevronRight,
  Home as HomeIcon,
  Hotel,
  Clock,
  ShieldCheck,
  AlertTriangle,
  Star,
} from "lucide-react";
import { COMPANY } from "../lib/constants";
import useSeo, { breadcrumbJsonLd, SITE } from "../lib/useSeo";

const TOC = [
  { id: "pourquoi", t: "Pourquoi un serrurier dédié à votre hôtel ?" },
  { id: "criteres", t: "Les 7 critères pour choisir le bon serrurier" },
  { id: "tarifs", t: "Tarifs et facturation pro à connaître" },
  { id: "urgences", t: "Urgences : les pièges à éviter" },
  { id: "saint-malo", t: "Spécificités d'un hôtel à Saint-Malo" },
  { id: "checklist", t: "Checklist : votre serrurier idéal en 10 points" },
];

export default function BlogHotelSaintMalo() {
  useSeo({
    title:
      "Comment choisir un serrurier pour son hôtel à Saint-Malo ? | Mon Serrurier Breton",
    description:
      "Le guide complet pour choisir un serrurier de confiance pour votre hôtel à Saint-Malo : critères techniques, tarifs, urgences nocturnes, organigrammes hôteliers, contrats cadres. Conseils d'un artisan local.",
    path: "/blog/serrurier-hotel-saint-malo",
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Accueil", path: "/" },
        { name: "Blog", path: "/blog/serrurier-hotel-saint-malo" },
        {
          name: "Serrurier hôtel Saint-Malo",
          path: "/blog/serrurier-hotel-saint-malo",
        },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline:
          "Comment choisir un serrurier pour son hôtel à Saint-Malo ?",
        description:
          "Guide complet pour les directeurs d'hôtels et hôteliers de Saint-Malo : critères de choix, tarifs, urgences, organigrammes hôteliers.",
        author: {
          "@type": "Organization",
          name: SITE.name,
          url: SITE.url,
        },
        publisher: {
          "@type": "Organization",
          name: SITE.name,
          logo: { "@type": "ImageObject", url: `${SITE.url}/logo.png` },
        },
        datePublished: "2026-05-05",
        dateModified: "2026-05-05",
        image: `${SITE.url}/og-image.jpg`,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${SITE.url}/blog/serrurier-hotel-saint-malo`,
        },
        about: [
          { "@type": "Thing", name: "Hôtellerie" },
          { "@type": "Place", name: "Saint-Malo" },
          { "@type": "Service", name: "Serrurerie professionnelle" },
        ],
      },
    ],
  });

  return (
    <article data-testid="page-blog-hotel-saint-malo">
      {/* Breadcrumb */}
      <nav
        className="max-w-5xl mx-auto px-4 md:px-8 pt-8 text-xs text-slate-500 flex items-center gap-2 flex-wrap"
        aria-label="Fil d'Ariane"
      >
        <Link to="/" className="inline-flex items-center gap-1 hover:text-teal-700">
          <HomeIcon className="w-3.5 h-3.5" /> Accueil
        </Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-slate-900 font-semibold">
          Serrurier hôtel Saint-Malo — Guide
        </span>
      </nav>

      {/* Hero */}
      <header className="max-w-5xl mx-auto px-4 md:px-8 pt-10 pb-10">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-4">
          <Hotel className="w-4 h-4" /> Guide hôtelier · 8 min de lecture
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[0.95]">
          Comment choisir un{" "}
          <span className="text-teal-600">serrurier pour son hôtel</span> à
          Saint-Malo ?
        </h1>
        <p className="text-slate-600 mt-6 text-lg leading-relaxed max-w-3xl">
          Que vous dirigiez un boutique-hôtel intra-muros, une chaîne en
          périphérie ou une résidence de tourisme sur la Côte d'Émeraude, le
          choix de votre serrurier conditionne autant la sécurité de vos
          clients que la fluidité de votre exploitation. Voici le guide
          complet, basé sur 800+ interventions réelles.
        </p>
        <div className="mt-6 flex items-center gap-4 text-sm text-slate-500">
          <span>Publié le 5 mai 2026</span>
          <span>·</span>
          <span>Par Mon Serrurier Breton</span>
        </div>
      </header>

      {/* Layout: TOC + content */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4 order-2 lg:order-1">
            <div className="lg:sticky lg:top-28 bg-slate-50 border border-slate-200 rounded-3xl p-6">
              <div className="text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
                Sommaire
              </div>
              <ol className="space-y-2 text-sm">
                {TOC.map((t, i) => (
                  <li key={t.id}>
                    <a
                      href={`#${t.id}`}
                      data-testid={`toc-${t.id}`}
                      className="flex items-start gap-3 text-slate-700 hover:text-teal-700 transition-colors"
                    >
                      <span className="font-display font-black text-slate-300 w-6 flex-shrink-0">
                        0{i + 1}
                      </span>
                      <span>{t.t}</span>
                    </a>
                  </li>
                ))}
              </ol>
              <div className="mt-6 pt-5 border-t border-slate-200">
                <div className="text-xs font-bold uppercase tracking-widest text-orange-600 mb-2">
                  Besoin d'aide immédiate ?
                </div>
                <a
                  href={`tel:${COMPANY.phoneDigits}`}
                  data-testid="blog-aside-call"
                  className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-5 py-3 shadow-lg shadow-orange-600/30 text-sm w-full justify-center"
                >
                  <Phone className="w-4 h-4" /> {COMPANY.phone}
                </a>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-8 order-1 lg:order-2 space-y-12 text-slate-700 leading-relaxed">
            <section id="pourquoi" className="scroll-mt-32">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-4">
                01 · Pourquoi un serrurier dédié à votre hôtel ?
              </h2>
              <p className="mb-4">
                Un hôtel n'est pas un logement classique. Vous gérez{" "}
                <strong>une rotation permanente de clés</strong> (chambres,
                suites, locaux techniques, coffres, parkings), une{" "}
                <strong>obligation de sécurité</strong> envers vos clients
                24h/24, et une <strong>tolérance zéro</strong> aux incidents
                visibles. Un serrurier généraliste ne suffit pas.
              </p>
              <p className="mb-4">
                Le serrurier hôtelier doit comprendre votre métier : la
                discrétion à 1h du matin quand un voyageur reste dehors, la
                priorité absolue avant l'arrivée d'un séminaire, la nécessité
                de préserver l'organigramme général sans tout refaire à chaque
                clé perdue. Il doit aussi connaître les{" "}
                <strong>technologies hôtelières spécifiques</strong> :
                serrures électroniques (Vingcard, Saflok, Onity), badges RFID,
                systèmes de gestion intégrés (PMS) comme Mews, Opera ou Misterbooking.
              </p>
              <p>
                À Saint-Malo, où l'hôtellerie est concentrée intra-muros et
                Saint-Servan, la majorité des hôtels sont indépendants ou
                liés à de petites chaînes. Un partenariat direct avec un
                <strong> artisan local</strong> évite les surcoûts des grands
                réseaux nationaux et garantit un délai d'intervention sous{" "}
                <strong>30 minutes</strong>.
              </p>
            </section>

            <section id="criteres" className="scroll-mt-32">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-4">
                02 · Les 7 critères pour choisir le bon serrurier
              </h2>
              <div className="space-y-4">
                {[
                  {
                    t: "Disponibilité 24/7 garantie",
                    d: "Un hôtel ne dort jamais. Vérifiez la ligne d'astreinte (numéro direct, pas plateforme) et le délai contractuel de prise en charge.",
                  },
                  {
                    t: "Compétence sur les serrures électroniques",
                    d: "Vingcard, Saflok, Onity, Salto, Dormakaba : chaque marque a ses spécificités. Demandez les références hôtelières du serrurier.",
                  },
                  {
                    t: "Maîtrise des organigrammes hôteliers",
                    d: "Passe général directeur, passes étages, passes étages, clés chambre : votre serrurier doit pouvoir documenter et faire évoluer votre organigramme sans tout refaire.",
                  },
                  {
                    t: "Discrétion et code de conduite",
                    d: "Tenue, comportement, présence à la réception : votre serrurier représente votre établissement face au client.",
                  },
                  {
                    t: "Facturation pro structurée",
                    d: "TVA, BAT, paiement à 30 jours, attestations URSSAF/décennale : indispensables pour les directions hôtelières et la comptabilité.",
                  },
                  {
                    t: "Tarification en contrat cadre",
                    d: "Évitez les facturations 'à la mission' opaques. Exigez une grille tarifaire négociée annuellement avec forfaits déplacement et main-d'œuvre.",
                  },
                  {
                    t: "Proximité géographique",
                    d: "Un serrurier basé à plus de 30 minutes ne pourra jamais respecter un SLA de 30 minutes. Privilégiez l'artisan local Côte d'Émeraude.",
                  },
                ].map((c, i) => (
                  <div
                    key={c.t}
                    className="flex items-start gap-4 bg-slate-50 border border-slate-200 rounded-2xl p-5"
                  >
                    <div className="w-9 h-9 rounded-xl bg-teal-600 text-white font-display font-extrabold flex items-center justify-center flex-shrink-0 text-sm">
                      0{i + 1}
                    </div>
                    <div>
                      <div className="font-display font-bold text-slate-900 mb-1">
                        {c.t}
                      </div>
                      <div className="text-sm text-slate-600">{c.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section id="tarifs" className="scroll-mt-32">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-4">
                03 · Tarifs et facturation pro à connaître
              </h2>
              <p className="mb-4">
                Les tarifs hôteliers fonctionnent en{" "}
                <strong>logique de volume</strong> : plus vous concentrez vos
                interventions chez un seul artisan, plus le coût unitaire
                baisse. Une grille type pour un hôtel à Saint-Malo se présente
                ainsi :
              </p>
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-4">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 text-slate-900 font-bold text-left">
                    <tr>
                      <th className="px-4 py-3">Prestation</th>
                      <th className="px-4 py-3">Tarif particulier</th>
                      <th className="px-4 py-3 bg-teal-50 text-teal-700">
                        Contrat hôtelier
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {[
                      ["Ouverture chambre fermée", "100 €", "65 €"],
                      ["Changement cylindre", "120 €", "85 €"],
                      ["Déplacement urgence nuit", "+50 %", "Forfait fixe"],
                      ["Audit annuel parc", "Devis", "Inclus"],
                    ].map((row, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3 font-semibold text-slate-900">
                          {row[0]}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{row[1]}</td>
                        <td className="px-4 py-3 text-teal-700 font-bold bg-teal-50/50">
                          {row[2]}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p>
                Comptez en moyenne <strong>20 à 35 % d'économie</strong> sur
                un contrat cadre par rapport à des appels ponctuels — sans
                compter le gain de temps de votre réception.
              </p>
            </section>

            <section id="urgences" className="scroll-mt-32">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-4">
                04 · Urgences : les pièges à éviter
              </h2>
              <div className="bg-orange-50 border-l-4 border-orange-500 rounded-r-2xl p-5 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-display font-bold text-slate-900 mb-1">
                      Attention aux pseudo-réseaux nationaux
                    </div>
                    <div className="text-sm text-slate-700">
                      Numéros surfacturés, déplacements à 200 €, devis triplés
                      sur place : un fléau qui touche l'hôtellerie quand la
                      réception cherche en urgence sur Google. Référencer un
                      artisan local <strong>en amont</strong> évite ces pièges.
                    </div>
                  </div>
                </div>
              </div>
              <p className="mb-4">
                <strong>Bonnes pratiques :</strong>
              </p>
              <ul className="space-y-2 mb-4">
                {[
                  "Affichez le numéro de votre serrurier référencé en backoffice réception (pas accessible aux clients).",
                  "Demandez systématiquement le devis annoncé par téléphone avant intervention.",
                  "Vérifiez la facture détaillée : déplacement + main-d'œuvre + fournitures.",
                  "Conservez photos avant/après pour assurance.",
                  "Privilégiez les paiements directs hôtel → serrurier (jamais via le client).",
                ].map((p) => (
                  <li key={p} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section id="saint-malo" className="scroll-mt-32">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-4">
                05 · Spécificités d'un hôtel à Saint-Malo
              </h2>
              <p className="mb-4">
                Saint-Malo concentre une diversité hôtelière unique en
                Bretagne : hôtels intra-muros (souvent dans des bâtiments
                classés aux fenêtres et portes anciennes), résidences de bord
                de mer, hôtels de gare à Saint-Servan, et hôtels de chaîne
                périphériques. Chaque typologie a ses contraintes :
              </p>
              <ul className="space-y-3 mb-4">
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center font-display font-extrabold text-sm flex-shrink-0">
                    A
                  </span>
                  <div>
                    <strong>Intra-muros</strong> : portes anciennes en bois
                    massif, cylindres souvent non standards. Il faut des
                    interventions sur-mesure, jamais de perçage destructif
                    sans accord patrimoine.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center font-display font-extrabold text-sm flex-shrink-0">
                    B
                  </span>
                  <div>
                    <strong>Façade mer</strong> : air salin agressif sur les
                    cylindres standards. Privilégier inox A2P et lubrification
                    semestrielle préventive.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center font-display font-extrabold text-sm flex-shrink-0">
                    C
                  </span>
                  <div>
                    <strong>Saisonnalité forte</strong> : avril-octobre =
                    pleine charge, le moindre incident bloque toute la
                    journée. Audit obligatoire fin mars.
                  </div>
                </li>
              </ul>
              <p>
                Un partenariat avec un serrurier basé à 15 minutes de
                Saint-Malo (comme Mon Serrurier Breton à Mont-Dol)
                garantit une intervention sous 30 minutes même en pleine
                saison estivale.
              </p>
            </section>

            <section id="checklist" className="scroll-mt-32">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-4">
                06 · Checklist : votre serrurier idéal en 10 points
              </h2>
              <div className="bg-teal-50 border border-teal-200 rounded-3xl p-6 md:p-8">
                <ul className="space-y-3">
                  {[
                    "Artisan local basé à moins de 30 km de votre hôtel",
                    "Référent unique nommé pour votre établissement",
                    "Astreinte 24/7 avec ligne directe (pas plateforme)",
                    "Maîtrise des serrures électroniques (Vingcard, Saflok, Onity)",
                    "Devis cadre annuel avec grille tarifaire négociée",
                    "Facturation à 30 jours fin de mois",
                    "Documentation organigramme à jour",
                    "Audit annuel inclus avec rapport PDF",
                    "Attestations Kbis, décennale, URSSAF à jour",
                    "Témoignages d'autres hôteliers de la zone",
                  ].map((p, i) => (
                    <li key={p} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-teal-600 text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold">
                        {i + 1}
                      </span>
                      <span className="text-slate-800 leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* CTA final */}
            <section className="scroll-mt-32">
              <div className="rounded-3xl bg-slate-900 text-white p-8 md:p-10 relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-amber-400/15 blur-3xl" />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-300 mb-3">
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                    Mon Serrurier Breton · Hôteliers Saint-Malo
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-extrabold mb-3 leading-tight">
                    Référencez-nous dès maintenant
                    <br />
                    <span className="text-amber-300">
                      en partenaire serrurier de votre hôtel.
                    </span>
                  </h3>
                  <p className="text-slate-300 mb-6 leading-relaxed max-w-2xl">
                    Devis cadre sous 24h · Audit gratuit sur site · Référent
                    unique 24/7 · Couverture toute la Côte d'Émeraude.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={`tel:${COMPANY.phoneDigits}`}
                      data-testid="blog-final-call"
                      className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-6 py-3.5 shadow-lg shadow-orange-600/30"
                    >
                      <Phone className="w-4 h-4" /> {COMPANY.phone}
                    </a>
                    <Link
                      to="/serrurier-hoteliers"
                      data-testid="blog-final-audience"
                      className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold rounded-full px-6 py-3.5"
                    >
                      Notre offre hôteliers <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </article>
  );
}
